import { useState, useEffect } from "react";
import apiClient from "@/utils/apiClient";
import downloadFile from "@/utils/fileDownloader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Heart, AlertCircle, Download, Upload, Clock, Search, Activity, CheckCircle, ShieldAlert, AlertTriangle, Users, Filter } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const HealthRecords = ({ permissions = {} }) => {
  const [students, setStudents] = useState([]);
  const [healthRecords, setHealthRecords] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // 'all' | 'attention' | 'healthy'
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkStartDate, setBulkStartDate] = useState('');
  const [bulkEndDate, setBulkEndDate] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState(null);
  const [formData, setFormData] = useState({
    student_id: "",
    date: new Date().toISOString().split('T')[0],
    status: "healthy",
    customStatus: "",
    symptoms: "",
    treatment: "",
    follow_up_required: false
  });

  const today = new Date().toISOString().split('T')[0];
  const canUpdate = permissions?.can_update_health || false;

  useEffect(() => {
    fetchStudents();
    fetchHealthRecords();
  }, []);

  // Set default selected student once records are loaded
  useEffect(() => {
    if (healthRecords.length > 0 && !selectedStudentId) {
      setSelectedStudentId(healthRecords[0].student_id);
    }
  }, [healthRecords]);

  const fetchStudents = async () => {
    try {
      const response = await apiClient.get('/v1/warden/students/my-students');
      setStudents(response.data.students || []);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchHealthRecords = async () => {
    try {
      const response = await apiClient.get('/v1/warden/health/recent', {
        params: { date: today }
      });
      setHealthRecords(response.data || []);
    } catch (error) {
      console.error(error);
    }
  };

  const openAddDialog = (targetStudentId = null) => {
    setFormData({
      student_id: targetStudentId || selectedStudentId || (students[0]?.id || ""),
      date: new Date().toISOString().split('T')[0],
      status: "healthy",
      customStatus: "",
      symptoms: "",
      treatment: "",
      follow_up_required: false
    });
    setShowAddDialog(true);
  };

  const handleAdd = async (e, forceUpdate = false) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const payload = {
        student_id: formData.student_id,
        date: formData.date,
        status: formData.status === 'custom' ? formData.customStatus : formData.status,
        symptoms: formData.symptoms || '',
        treatment: formData.treatment || '',
        follow_up_required: formData.follow_up_required,
        force_update: forceUpdate
      };
      
      const response = await apiClient.post('/v1/warden/health/add', payload);
      
      if (response.data.duplicate_found && !forceUpdate) {
        setDuplicateInfo({
          existing: response.data.existing_record,
          message: response.data.message
        });
        setShowConfirmDialog(true);
        setLoading(false);
        return;
      }
      
      toast.success("Health record added successfully");
      setShowAddDialog(false);
      setShowConfirmDialog(false);
      setDuplicateInfo(null);
      
      // Select the student we just added/updated
      setSelectedStudentId(formData.student_id);
      
      setFormData({
        student_id: "",
        date: new Date().toISOString().split('T')[0],
        status: "healthy",
        customStatus: "",
        symptoms: "",
        treatment: "",
        follow_up_required: false
      });
      fetchHealthRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add health record");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmOverwrite = (e) => {
    setShowConfirmDialog(false);
    handleAdd(e, true);
  };

  const handleBulkDownload = async () => {
    if (!bulkStartDate || !bulkEndDate) {
      toast.error("Please select start and end dates");
      return;
    }
    
    try {
      const response = await apiClient.get('/v1/warden/health/template', {
        params: {
          start_date: bulkStartDate,
          end_date: bulkEndDate
        },
        responseType: 'blob'
      });
      
      const fileName = `health_template_${bulkStartDate}_to_${bulkEndDate}.xlsx`;
      const mimeType = response.headers?.['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      await downloadFile(response.data, fileName, mimeType);
    } catch (error) {
      toast.error("Failed to download template");
    }
  };

  const handleBulkUpload = async () => {
    if (!bulkFile) {
      toast.error("Please select a file");
      return;
    }

    const formData = new FormData();
    formData.append('file', bulkFile);

    setLoading(true);
    try {
      const response = await apiClient.post('/v1/warden/health/bulk-upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(`Health records uploaded: ${response.data.successful} successful, ${response.data.failed} failed`);
      setBulkFile(null);
      fetchHealthRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload health records");
    } finally {
      setLoading(false);
    }
  };

  const getStatusStyle = (status) => {
    const key = status ? String(status).toLowerCase().trim() : '';
    switch (key) {
      case 'healthy':
        return {
          bg: 'bg-green-50 text-green-700 border-green-200',
          dot: 'bg-green-500',
          icon: <CheckCircle className="w-4 h-4 text-green-600" />,
          pill: 'bg-green-100 text-green-800'
        };
      case 'sick':
        return {
          bg: 'bg-red-50 text-red-700 border-red-200',
          dot: 'bg-red-500',
          icon: <Activity className="w-4 h-4 text-red-600" />,
          pill: 'bg-red-100 text-red-800'
        };
      case 'injured':
        return {
          bg: 'bg-orange-50 text-orange-700 border-orange-200',
          dot: 'bg-orange-500',
          icon: <AlertTriangle className="w-4 h-4 text-orange-600" />,
          pill: 'bg-orange-100 text-orange-800'
        };
      case 'unwell':
        return {
          bg: 'bg-yellow-50 text-yellow-700 border-yellow-200',
          dot: 'bg-yellow-500',
          icon: <AlertCircle className="w-4 h-4 text-yellow-600" />,
          pill: 'bg-yellow-100 text-yellow-800'
        };
      default:
        return {
          bg: 'bg-blue-50 text-blue-700 border-blue-200',
          dot: 'bg-blue-500',
          icon: <Heart className="w-4 h-4 text-blue-600" />,
          pill: 'bg-blue-100 text-blue-800'
        };
    }
  };

  const totalCount = healthRecords.length;
  const healthyCount = healthRecords.filter(s => String(s.current_status || '').toLowerCase() === 'healthy').length;
  const attentionCount = healthRecords.filter(s => String(s.current_status || '').toLowerCase() !== 'healthy').length;
  const followUpCount = healthRecords.filter(s => s.records?.some(r => r.follow_up_required)).length;

  const filteredStudents = healthRecords.filter(student => {
    const query = searchQuery.toLowerCase();
    const matchesSearch = student.student_name.toLowerCase().includes(query) ||
      (student.readable_id && student.readable_id.toLowerCase().includes(query));
    
    if (!matchesSearch) return false;
    
    if (statusFilter === 'attention') {
      return String(student.current_status || '').toLowerCase() !== 'healthy' ||
        student.records?.some(r => r.follow_up_required);
    }
    if (statusFilter === 'healthy') {
      return String(student.current_status || '').toLowerCase() === 'healthy';
    }
    return true;
  });

  const selectedStudent = healthRecords.find(s => s.student_id === selectedStudentId);

  // Calculate statistics for selected student
  const getSelectedStudentStats = () => {
    if (!selectedStudent) return { total: 0, healthy: 0, sick: 0, rate: 100 };
    const records = selectedStudent.records || [];
    const total = records.length;
    const sick = records.filter(r => ['sick', 'injured', 'unwell'].includes(String(r.status).toLowerCase().trim())).length;
    const healthy = total - sick;
    const rate = total === 0 ? 100 : Math.max(0, Math.round(((total - sick) / total) * 100));
    return { total, healthy, sick, rate };
  };

  const stats = getSelectedStudentStats();

  if (!canUpdate) {
    return (
      <div className="p-6">
        <Card className="border-0 shadow-lg">
          <CardContent className="p-12 text-center space-y-4">
            <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto">
              <ShieldAlert className="w-10 h-10 text-red-500" />
            </div>
            <h3 className="text-2xl font-bold text-gray-800">Access Restricted</h3>
            <p className="text-gray-600 max-w-md mx-auto">
              You do not have the <strong>can_update_health</strong> permission required to manage student health records.
            </p>
            <p className="text-sm text-gray-400">Please contact the hostel administrator for access.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-7xl mx-auto p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 bg-white p-6 rounded-2xl border shadow-sm">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-[#2C5F7F]">Student Health Logs</h1>
          <p className="text-sm text-gray-500 mt-1">
            System automatically assumes students are <strong>Healthy</strong> unless marked sick, injured, or under treatment.
          </p>
        </div>
        <Button onClick={() => openAddDialog()} className="bg-[#2C5F7F] hover:bg-[#204760] text-white self-start md:self-auto shadow-md rounded-xl transition-all py-5">
          <Plus className="w-5 h-5 mr-2" />
          Add Health Record
        </Button>
      </div>

      {/* Overview KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-2xl border shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Assigned Students</p>
            <p className="text-2xl font-black text-gray-800 mt-1">{totalCount}</p>
            <p className="text-[11px] text-gray-400 mt-0.5">Total in your hostel</p>
          </div>
          <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
            <Users className="w-5 h-5" />
          </div>
        </div>

        <div className="bg-white p-4 rounded-2xl border shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wide">Healthy Today</p>
            <p className="text-2xl font-black text-emerald-600 mt-1">{healthyCount}</p>
            <p className="text-[11px] text-emerald-600/80 mt-0.5">Auto-default & verified</p>
          </div>
          <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
            <CheckCircle className="w-5 h-5" />
          </div>
        </div>

        <div className="bg-white p-4 rounded-2xl border shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-rose-600 uppercase tracking-wide">Needs Attention</p>
            <p className="text-2xl font-black text-rose-600 mt-1">{attentionCount}</p>
            <p className="text-[11px] text-rose-600/80 mt-0.5">Reported unwell today</p>
          </div>
          <div className="w-10 h-10 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center">
            <Activity className="w-5 h-5" />
          </div>
        </div>

        <div className="bg-white p-4 rounded-2xl border shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-amber-600 uppercase tracking-wide">Pending Follow-ups</p>
            <p className="text-2xl font-black text-amber-600 mt-1">{followUpCount}</p>
            <p className="text-[11px] text-amber-600/80 mt-0.5">Requires checkup</p>
          </div>
          <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center">
            <AlertTriangle className="w-5 h-5" />
          </div>
        </div>
      </div>

      <Tabs defaultValue="individual" className="w-full">
        <TabsList className="bg-gray-100 p-1 rounded-xl mb-6 grid w-full max-w-md grid-cols-2 shadow-inner">
          <TabsTrigger value="individual" className="rounded-lg py-2 font-medium">Individual View</TabsTrigger>
          <TabsTrigger value="bulk" className="rounded-lg py-2 font-medium">Bulk Upload</TabsTrigger>
        </TabsList>

        <TabsContent value="individual" className="focus-visible:outline-none">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
            
            {/* LEFT COLUMN: Student Directory */}
            <div className="lg:col-span-4 bg-white rounded-2xl border shadow-sm flex flex-col max-h-[750px]">
              <div className="p-4 border-b space-y-3">
                <div className="flex items-center justify-between">
                  <h2 className="font-bold text-gray-800 text-lg flex items-center gap-2">
                    <Activity className="w-5 h-5 text-[#2C5F7F]" />
                    Students Directory
                  </h2>
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                    {filteredStudents.length}
                  </span>
                </div>

                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search student or ID..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 bg-gray-50/50 focus-visible:ring-1 border-gray-200 focus-visible:ring-[#2C5F7F] rounded-lg text-sm"
                  />
                </div>

                {/* Filter chips */}
                <div className="flex gap-1.5 pt-1">
                  <button
                    type="button"
                    onClick={() => setStatusFilter("all")}
                    className={`text-xs px-2.5 py-1 rounded-lg font-medium transition-colors ${
                      statusFilter === 'all'
                        ? 'bg-[#2C5F7F] text-white shadow-xs'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    All ({totalCount})
                  </button>
                  <button
                    type="button"
                    onClick={() => setStatusFilter("attention")}
                    className={`text-xs px-2.5 py-1 rounded-lg font-medium transition-colors ${
                      statusFilter === 'attention'
                        ? 'bg-rose-600 text-white shadow-xs'
                        : 'bg-rose-50 text-rose-700 hover:bg-rose-100'
                    }`}
                  >
                    Attention ({attentionCount})
                  </button>
                  <button
                    type="button"
                    onClick={() => setStatusFilter("healthy")}
                    className={`text-xs px-2.5 py-1 rounded-lg font-medium transition-colors ${
                      statusFilter === 'healthy'
                        ? 'bg-emerald-700 text-white shadow-xs'
                        : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                    }`}
                  >
                    Healthy ({healthyCount})
                  </button>
                </div>
              </div>

              <div className="overflow-y-auto flex-1 p-2 space-y-1 custom-scrollbar">
                {filteredStudents.length === 0 ? (
                  <p className="text-center text-sm text-gray-500 py-8">No students found matching filter</p>
                ) : (
                  filteredStudents.map((student) => {
                    const currentStatus = student.current_status || 'healthy';
                    const isDefault = student.is_default_status;
                    const statusStyle = getStatusStyle(currentStatus);
                    const isSelected = selectedStudentId === student.student_id;
                    const hasFollowUp = student.records?.some(r => r.follow_up_required);
                    
                    return (
                      <button
                        key={student.student_id}
                        onClick={() => setSelectedStudentId(student.student_id)}
                        className={`w-full text-left p-3 rounded-xl transition-all flex items-center justify-between border ${
                          isSelected
                            ? "bg-blue-50/70 border-blue-200 shadow-sm"
                            : "hover:bg-gray-50/50 border-transparent"
                        }`}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <p className="font-bold text-gray-800 truncate text-sm">{student.student_name}</p>
                            {hasFollowUp && (
                              <span title="Pending follow-up" className="w-2 h-2 rounded-full bg-amber-500 shrink-0" />
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                            <span>ID: {student.readable_id || 'N/A'}</span>
                            <span>•</span>
                            {student.today_record ? (
                              <span className="text-emerald-700 font-semibold">Logged today</span>
                            ) : student.latest_record ? (
                              <span className="truncate">Last log: {student.latest_record.date}</span>
                            ) : (
                              <span className="text-gray-400">Default Healthy</span>
                            )}
                          </div>
                        </div>

                        <span className={`ml-2 px-2.5 py-0.5 text-[10px] font-bold rounded-full capitalize border ${statusStyle.bg} flex items-center gap-1 shrink-0`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${statusStyle.dot}`} />
                          {currentStatus}
                          {isDefault && <span className="text-[9px] opacity-75 font-normal">(Auto)</span>}
                        </span>
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            {/* RIGHT COLUMN: Health Details Timeline */}
            <div className="lg:col-span-8 space-y-6">
              {selectedStudent ? (
                <>
                  {/* Selected Student Information Panel */}
                  <div className="bg-white rounded-2xl border shadow-sm p-6 space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b">
                      <div>
                        <h2 className="text-2xl font-bold text-gray-800">{selectedStudent.student_name}</h2>
                        <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                          <span className="bg-blue-50 text-blue-700 px-2.5 py-0.5 rounded-full text-xs font-semibold border border-blue-100">
                            ID: {selectedStudent.readable_id || 'N/A'}
                          </span>
                          {selectedStudent.room_number && (
                            <span className="bg-gray-100 text-gray-600 px-2.5 py-0.5 rounded-full text-xs font-medium">
                              Room {selectedStudent.room_number}
                            </span>
                          )}
                          {selectedStudent.class_name && (
                            <span className="bg-gray-100 text-gray-600 px-2.5 py-0.5 rounded-full text-xs font-medium">
                              Class {selectedStudent.class_name}
                            </span>
                          )}
                          {selectedStudent.latest_record && (
                            <span className="text-gray-500 text-xs flex items-center gap-1">
                              <Clock className="w-3.5 h-3.5" />
                              Last record on {selectedStudent.latest_record.date}
                            </span>
                          )}
                        </div>
                      </div>
                      <Button onClick={() => openAddDialog(selectedStudent.student_id)} variant="outline" className="border-gray-200 text-gray-700 hover:bg-gray-50 hover:text-gray-900 rounded-xl">
                        <Plus className="w-4 h-4 mr-2" />
                        Log Status
                      </Button>
                    </div>

                    {/* Today's Status Banner */}
                    <div className={`p-4 rounded-xl border flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${getStatusStyle(selectedStudent.current_status).bg}`}>
                      <div className="flex items-start sm:items-center gap-3">
                        <div className="p-2 bg-white/80 rounded-lg shadow-sm">
                          {getStatusStyle(selectedStudent.current_status).icon}
                        </div>
                        <div>
                          <p className="font-bold text-sm text-gray-900 flex items-center gap-2 flex-wrap">
                            Today's Status: <span className="capitalize">{selectedStudent.current_status || 'Healthy'}</span>
                            {selectedStudent.is_default_status ? (
                              <span className="text-[11px] font-medium text-emerald-800 bg-emerald-100 px-2 py-0.5 rounded-full border border-emerald-200">
                                Auto-assigned (No report today)
                              </span>
                            ) : (
                              <span className="text-[11px] font-medium text-blue-800 bg-blue-100 px-2 py-0.5 rounded-full border border-blue-200">
                                Explicitly logged today
                              </span>
                            )}
                          </p>
                          <p className="text-xs text-gray-600 mt-0.5">
                            {selectedStudent.today_record
                              ? (selectedStudent.today_record.symptoms ? `Symptoms: ${selectedStudent.today_record.symptoms}` : "Logged for today by warden.")
                              : "No illness or treatment reported today. Student is in good health."
                            }
                          </p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => openAddDialog(selectedStudent.student_id)}
                        className="bg-[#2C5F7F] hover:bg-[#204760] text-white text-xs shrink-0 self-start sm:self-auto rounded-lg shadow-xs"
                      >
                        {selectedStudent.today_record ? "Update Today's Status" : "Log Issue for Today"}
                      </Button>
                    </div>

                    {/* Stats summary row */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div className="bg-green-50/50 border border-green-100 p-4 rounded-xl">
                        <p className="text-xs text-green-700 font-semibold uppercase tracking-wider">Health Rating</p>
                        <p className="text-3xl font-extrabold text-green-700 mt-1">{stats.rate}%</p>
                        <p className="text-xs text-green-600 mt-1">
                          {selectedStudent.records?.length === 0 ? "Clean medical history" : "Incident-free percentage"}
                        </p>
                      </div>
                      <div className="bg-blue-50/50 border border-blue-100 p-4 rounded-xl">
                        <p className="text-xs text-blue-700 font-semibold uppercase tracking-wider">Logged Incidents</p>
                        <p className="text-3xl font-extrabold text-blue-700 mt-1">{stats.sick}</p>
                        <p className="text-xs text-blue-600 mt-1">Past illness or injury logs</p>
                      </div>
                      <div className="bg-gray-50/50 border border-gray-100 p-4 rounded-xl">
                        <p className="text-xs text-gray-700 font-semibold uppercase tracking-wider">Total Health Logs</p>
                        <p className="text-3xl font-extrabold text-gray-800 mt-1">{stats.total}</p>
                        <p className="text-xs text-gray-500 mt-1">Specific records on file</p>
                      </div>
                    </div>
                  </div>

                  {/* Health Timeline Card */}
                  <Card className="border shadow-sm rounded-2xl bg-white overflow-hidden">
                    <CardHeader className="bg-gray-50/50 border-b p-5">
                      <CardTitle className="text-lg font-bold text-gray-800 flex items-center gap-2">
                        <Heart className="w-5 h-5 text-red-500" />
                        Medical History Timeline
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-6">
                      {selectedStudent.records?.length === 0 ? (
                        <div className="text-center py-12 text-gray-400">
                          <CheckCircle className="w-12 h-12 mx-auto text-emerald-400 mb-3" />
                          <h4 className="font-bold text-gray-700 text-base">Clean Medical History</h4>
                          <p className="text-sm text-gray-500 max-w-sm mx-auto mt-1">
                            No medical incidents or sickness reports have been logged for this student. They are marked Healthy by default.
                          </p>
                          <Button onClick={() => openAddDialog(selectedStudent.student_id)} className="mt-4 bg-[#2C5F7F] text-white rounded-xl shadow-xs">
                            Log Health Issue
                          </Button>
                        </div>
                      ) : (
                        <div className="relative border-l-2 border-blue-100 ml-4 pl-6 space-y-6">
                          {selectedStudent.records.map((record) => {
                            const style = getStatusStyle(record.status);
                            return (
                              <div key={record.id} className="relative group">
                                {/* Timeline Indicator Dot */}
                                <div className={`absolute -left-[31px] top-1 w-4 h-4 rounded-full border-2 border-white ring-4 ring-white ${style.dot}`} />
                                
                                <div className="bg-white border rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow relative">
                                  <div className="flex justify-between items-start flex-wrap gap-2 mb-2">
                                    <span className="font-bold text-sm text-[#2C5F7F] bg-blue-50 px-2 py-0.5 rounded">
                                      {record.date}
                                    </span>
                                    <span className={`px-2.5 py-0.5 text-xs font-bold rounded-full capitalize border ${style.bg}`}>
                                      {record.status}
                                    </span>
                                  </div>

                                  <div className="space-y-2.5 text-sm">
                                    {record.symptoms || record.notes || record.description ? (
                                      <p className="text-gray-700 bg-gray-50 p-2.5 rounded-lg border border-gray-100">
                                        <strong className="text-gray-600">Symptoms:</strong> {record.symptoms || record.notes || record.description}
                                      </p>
                                    ) : (
                                      <p className="text-gray-400 italic">No symptoms or notes logged.</p>
                                    )}

                                    {(record.treatment || record.visit_details) && (
                                      <p className="text-gray-700 bg-blue-50/30 p-2.5 rounded-lg border border-blue-50">
                                        <strong className="text-blue-900">Treatment:</strong> {record.treatment || record.visit_details}
                                      </p>
                                    )}

                                    {record.follow_up_required && (
                                      <div className="mt-3 flex items-center gap-2 text-xs font-semibold text-orange-600 bg-orange-50 border border-orange-100 p-2 rounded-lg">
                                        <AlertTriangle className="w-4 h-4 text-orange-500 shrink-0 animate-pulse" />
                                        <span>Follow-up action required by Warden.</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </>
              ) : (
                <Card className="border-dashed border-2 p-12 text-center text-gray-500 rounded-2xl">
                  <Activity className="w-16 h-16 mx-auto text-gray-300 mb-4 animate-pulse" />
                  <h3 className="font-bold text-lg text-gray-800">No Student Selected</h3>
                  <p className="text-sm text-gray-500 mt-1">Select a student from the directory on the left to see health analytics and timelines.</p>
                </Card>
              )}
            </div>
            
          </div>
        </TabsContent>

        <TabsContent value="bulk" className="focus-visible:outline-none">
          <Card className="border shadow-sm rounded-2xl bg-white overflow-hidden">
            <CardHeader className="bg-gray-50/50 border-b p-5">
              <CardTitle className="text-lg font-bold text-gray-800">Bulk Ingestion Module</CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              
              <div className="bg-blue-50 border border-blue-100 p-5 rounded-2xl flex gap-3">
                <CheckCircle className="w-6 h-6 text-blue-600 shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-bold text-blue-900 text-sm mb-1">📋 Instructions & Formatting:</h3>
                  <ol className="text-xs text-blue-800 space-y-1.5 ml-4 list-decimal">
                    <li>Define the starting date and ending date to generate columns.</li>
                    <li>Download the template. The layout maps dates horizontally and lists students down the rows.</li>
                    <li>Write the status string under the date columns (options: <span className="font-semibold underline">healthy, sick, injured</span> or custom strings).</li>
                    <li>Save the spreadsheet as an Excel file and upload it below.</li>
                  </ol>
                </div>
              </div>

              {/* Date selection layout */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-gray-50 p-5 rounded-2xl border">
                <div className="space-y-1.5">
                  <Label className="text-sm font-semibold text-gray-700">Start Date</Label>
                  <input
                    type="date"
                    max={today}
                    value={bulkStartDate}
                    onChange={(e) => setBulkStartDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 bg-white rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2C5F7F] text-sm"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-semibold text-gray-700">End Date</Label>
                  <input
                    type="date"
                    max={today}
                    min={bulkStartDate || ''}
                    value={bulkEndDate}
                    onChange={(e) => setBulkEndDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 bg-white rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2C5F7F] text-sm"
                  />
                </div>
              </div>

              {bulkStartDate && bulkEndDate && (
                <div className="bg-gray-50 border p-3 rounded-lg flex items-center justify-between text-xs text-gray-600">
                  <span className="font-medium text-gray-700">Configuration Details:</span>
                  <span>{Math.ceil((new Date(bulkEndDate) - new Date(bulkStartDate)) / (1000 * 60 * 60 * 24)) + 1} Columns (Days) × {students.length} Student Rows</span>
                </div>
              )}

              <Button 
                onClick={handleBulkDownload} 
                className="w-full bg-white hover:bg-gray-50 text-gray-800 border border-gray-200 font-semibold py-3 shadow-sm rounded-xl transition-all"
                disabled={!bulkStartDate || !bulkEndDate}
              >
                <Download className="w-4 h-4 mr-2 text-gray-600" />
                Generate Template Spreadsheet
              </Button>

              <div className="border-2 border-dashed border-gray-200 bg-gray-50/50 hover:bg-gray-50 rounded-2xl p-8 text-center transition-colors">
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => setBulkFile(e.target.files[0])}
                  className="hidden"
                  id="health-bulk-file"
                />
                <label htmlFor="health-bulk-file" className="cursor-pointer space-y-3 block">
                  <Upload className="w-10 h-10 mx-auto text-[#2C5F7F] opacity-70" />
                  <div>
                    <p className="text-sm font-semibold text-gray-700">
                      {bulkFile ? bulkFile.name : "Select template file to upload"}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">Accepts Excel (.xlsx, .xls) files up to 10MB</p>
                  </div>
                </label>
              </div>

              {bulkFile && (
                <Button onClick={handleBulkUpload} disabled={loading} className="w-full bg-[#2C5F7F] hover:bg-[#204760] text-white py-3 shadow-md rounded-xl transition-all font-semibold">
                  {loading ? "Uploading & Ingesting..." : "Process Bulk Upload"}
                </Button>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Health Record Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-gray-800">Log Health Status</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAdd} className="space-y-4 pt-2">
            <div className="space-y-1.5">
              <Label className="font-semibold text-xs text-gray-500 uppercase tracking-wide">Select Student *</Label>
              <select
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#2C5F7F]"
                value={formData.student_id}
                onChange={(e) => setFormData({ ...formData, student_id: e.target.value })}
                required
              >
                <option value="">Choose Student...</option>
                {(students.length > 0 ? students : healthRecords.map(h => ({ id: h.student_id, name: h.student_name, class_name: h.class_name }))).map(student => (
                  <option key={student.id} value={student.id}>
                    {student.name} {student.class_name ? `- Class ${student.class_name}` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <Label className="font-semibold text-xs text-gray-500 uppercase tracking-wide">Date of Check *</Label>
              <Input
                type="date"
                max={today}
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
                className="rounded-lg text-sm"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="font-semibold text-xs text-gray-500 uppercase tracking-wide">Status Check *</Label>
              <select
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#2C5F7F]"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value, customStatus: '' })}
                required
              >
                <option value="healthy">Healthy</option>
                <option value="sick">Sick</option>
                <option value="injured">Injured</option>
                <option value="unwell">Unwell</option>
                <option value="custom">Custom</option>
              </select>
            </div>

            {formData.status === 'custom' && (
              <div className="space-y-1.5">
                <Label className="font-semibold text-xs text-gray-500 uppercase tracking-wide">Custom Health Status *</Label>
                <Input
                  value={formData.customStatus}
                  onChange={(e) => setFormData({ ...formData, customStatus: e.target.value })}
                  placeholder="Specify status..."
                  required
                  className="rounded-lg text-sm"
                />
              </div>
            )}

            <div className="space-y-1.5">
              <Label className="font-semibold text-xs text-gray-500 uppercase tracking-wide">Symptoms {formData.status !== 'healthy' && '*'}</Label>
              <Textarea
                value={formData.symptoms}
                onChange={(e) => setFormData({ ...formData, symptoms: e.target.value })}
                placeholder="List complaints or symptom descriptions..."
                required={formData.status !== 'healthy'}
                className="rounded-lg text-sm min-h-[80px]"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="font-semibold text-xs text-gray-500 uppercase tracking-wide">Treatment Administered</Label>
              <Textarea
                value={formData.treatment}
                onChange={(e) => setFormData({ ...formData, treatment: e.target.value })}
                placeholder="Prescription, first aid, rest given..."
                className="rounded-lg text-sm min-h-[60px]"
              />
            </div>

            <div className="flex items-center gap-2.5 pt-1">
              <input
                type="checkbox"
                id="follow-up"
                checked={formData.follow_up_required}
                onChange={(e) => setFormData({ ...formData, follow_up_required: e.target.checked })}
                className="w-4 h-4 rounded text-[#2C5F7F] focus:ring-[#2C5F7F] border-gray-300"
              />
              <Label htmlFor="follow-up" className="cursor-pointer text-sm font-medium text-gray-700">Requires follow-up attention</Label>
            </div>

            <Button type="submit" disabled={loading} className="w-full bg-[#2C5F7F] hover:bg-[#204760] text-white py-3 shadow-md rounded-xl transition-all font-semibold mt-2">
              {loading ? "Saving Health Log..." : "Log Health Entry"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog for Duplicates */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-gray-800">Duplicate Check Detected</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-gray-600 leading-relaxed">{duplicateInfo?.message}</p>
            {duplicateInfo?.existing && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 space-y-1.5">
                <p className="font-semibold text-xs text-yellow-800 uppercase tracking-wider">Existing Entry Details:</p>
                <div className="text-xs space-y-1 text-yellow-900">
                  <p><strong>Status:</strong> <span className="capitalize">{duplicateInfo.existing.status}</span></p>
                  <p><strong>Symptoms:</strong> {duplicateInfo.existing.symptoms || 'None recorded'}</p>
                  <p><strong>Treatment:</strong> {duplicateInfo.existing.treatment || 'None recorded'}</p>
                </div>
              </div>
            )}
            <div className="flex gap-2 justify-end pt-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowConfirmDialog(false);
                  setDuplicateInfo(null);
                }}
                className="rounded-xl border-gray-200"
              >
                Keep Existing
              </Button>
              <Button 
                onClick={(e) => handleConfirmOverwrite(e)}
                className="bg-[#2C5F7F] hover:bg-[#204760] text-white rounded-xl shadow-md"
              >
                Yes, Overwrite Record
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HealthRecords;
